/**
 * @file camera-decoders.ts
 * @description Декодеры кадров камеры для скана в поле склада (phase19.22
 *   шаг 5): встроенный `BarcodeDetector` и запасной `@zxing/browser`.
 *
 * Оба декодера читают один и тот же `<video>` со стримом, который открыл
 * `useCameraScanner`: стрим (и фонарик) принадлежит хуку, декодер только
 * смотрит в кадр. `stop()` декодера стрим НЕ останавливает — это делает хук.
 *
 * `@zxing/browser` (~400 КБ) грузится динамическим `import()` только при
 * открытии диалога и только там, где нет встроенного детектора (iOS Safari,
 * Firefox, Chrome на Windows) — в основной бандл админки он не попадает.
 */

import { CAMERA_FORMATS, nativeFormats, pickDecoderKind, type DecoderKind } from "./camera-scan";

/** Запущенный декодер. */
export interface DecoderHandle {
  stop: () => void;
}

/** Колбэк распознавания: сырой текст кода. */
export type OnDecoded = (raw: string) => void;

/** Опрос кадров встроенным детектором: ~10 раз в секунду. */
const NATIVE_POLL_MS = 100;

/** Форматы, которые поддерживает встроенный детектор (`null` — не узнали). */
async function nativeSupportedFormats(): Promise<string[] | null> {
  const Ctor = window.BarcodeDetector;
  if (!Ctor?.getSupportedFormats) return null;
  try {
    return await Ctor.getSupportedFormats();
  } catch {
    return null;
  }
}

/** Выбрать декодер для этого браузера. */
export async function detectDecoderKind(): Promise<{ kind: DecoderKind; supported: string[] }> {
  const supported = await nativeSupportedFormats();
  const kind = pickDecoderKind({
    hasBarcodeDetector: typeof window.BarcodeDetector === "function",
    supportedFormats: supported,
  });
  return { kind, supported: supported ?? [] };
}

/** Встроенный детектор: опрос кадров по таймеру до первого результата. */
export function startNativeDecoder(
  video: HTMLVideoElement,
  supported: readonly string[],
  onDecoded: OnDecoded,
): DecoderHandle {
  const Ctor = window.BarcodeDetector;
  if (!Ctor) throw new Error("BarcodeDetector недоступен");
  const detector = new Ctor({ formats: nativeFormats(supported) });
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const tick = async () => {
    if (stopped) return;
    // HAVE_CURRENT_DATA: кадр есть — можно детектировать.
    if (video.readyState >= 2) {
      try {
        const found = await detector.detect(video);
        const hit = found.find((b) => b.rawValue);
        if (hit && !stopped) {
          onDecoded(hit.rawValue);
          return;
        }
      } catch {
        // Пустой/битый кадр — пробуем следующий.
      }
    }
    if (!stopped) timer = setTimeout(() => void tick(), NATIVE_POLL_MS);
  };
  void tick();

  return {
    stop: () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    },
  };
}

/** Запасной декодер `@zxing/browser` — грузится только здесь. */
export async function startZxingDecoder(
  video: HTMLVideoElement,
  onDecoded: OnDecoded,
): Promise<DecoderHandle> {
  const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
    import("@zxing/browser"),
    import("@zxing/library"),
  ]);
  const ZXING_FORMAT: Record<(typeof CAMERA_FORMATS)[number], number> = {
    data_matrix: BarcodeFormat.DATA_MATRIX,
    code_128: BarcodeFormat.CODE_128,
    qr_code: BarcodeFormat.QR_CODE,
    ean_13: BarcodeFormat.EAN_13,
    ean_8: BarcodeFormat.EAN_8,
  };
  const hints = new Map<number, unknown>([
    [DecodeHintType.POSSIBLE_FORMATS, CAMERA_FORMATS.map((f) => ZXING_FORMAT[f])],
    [DecodeHintType.TRY_HARDER, true],
  ]);
  const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: NATIVE_POLL_MS });
  const controls = await reader.decodeFromVideoElement(video, (result) => {
    if (result) onDecoded(result.getText());
  });
  return { stop: () => controls.stop() };
}
